import { HFlow, Tag, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { HLabel } from 'components/HLabel'
import { Cns, Cpf, Telefone } from 'components/label'
import { intervalToDuration } from 'date-fns'
import { css } from 'emotion'
import { AcompanhamentoCondicaoSaudeTematicaEnum, GrupoCondicaoEnum } from 'graphql/types.generated'
import { Fragment } from 'react'
import { toDate } from 'util/date/formatDate'
import { formatDurationToMonthsAndDays } from 'util/date/formatDuration'
import { humanizeAge } from 'util/date/humanize-age'

import { useAcompanhamentoCondicaoSaudeContext } from '../../context/AcompanhamentoCondicaoSaudeContext'
import { BuscaCidadaoQueryResultModel } from '../../model/lista-tematica/model-acompanhamentoCondicaoSaudeListaTematica'
import {
  formatUltimaMedicaoPesoAltura,
  formatUltimaMedicaoPressaoArterial,
  hasProblemaAtivoDoGrupoCondicao,
} from './utils-acompanhamentoCondicaoSaudeTable'

const grupoCondicaoByTematica: Partial<Record<AcompanhamentoCondicaoSaudeTematicaEnum, GrupoCondicaoEnum>> = {
  [AcompanhamentoCondicaoSaudeTematicaEnum.HIPERTENSAO_ARTERIAL]: GrupoCondicaoEnum.HIPERTENSAO_ARTERIAL,
  [AcompanhamentoCondicaoSaudeTematicaEnum.DIABETES]: GrupoCondicaoEnum.DIABETES,
}
interface InfoCpfCnsAcompanhamentoCondicaoSaudeProps {
  row: BuscaCidadaoQueryResultModel
}

export const InfoCpfCnsAcompanhamentoCondicaoSaude = (props: InfoCpfCnsAcompanhamentoCondicaoSaudeProps) => {
  const { row } = props
  const { tematica } = useAcompanhamentoCondicaoSaudeContext()

  const theme = useTheme()
  const styles = createStyles(theme)

  const cpfComponent = row.cpfCidadao && (
    <HFlow hSpacing={0.25} alignItems='center'>
      <Text fontWeight='bold'>CPF</Text>
      <Cpf value={row.cpfCidadao} />
    </HFlow>
  )
  const cnsComponent = row.cnsCidadao && (
    <HFlow hSpacing={0.25} alignItems='center'>
      <Text fontWeight='bold'>CNS</Text>
      <Cns value={row.cnsCidadao} />
    </HFlow>
  )

  const hasNenhumProblemaAtivoDoGrupoCondicao = !hasProblemaAtivoDoGrupoCondicao(row, grupoCondicaoByTematica[tematica])

  return (
    <VFlow vSpacing={0}>
      <HFlow hSpacing={0.25} alignItems='center'>
        <Text fontWeight='bold'>{(row.nomeSocialCidadao ?? row.nomeCidadao)?.titleCase()}</Text>
        {hasNenhumProblemaAtivoDoGrupoCondicao && (
          <Tooltip text='Problema/condição não incluído na lista de problemas e condições'>
            <Tag icon='exclamationTriangleFilled' style={styles.warningTag} />
          </Tooltip>
        )}
      </HFlow>
      {cpfComponent ?? cnsComponent ?? <Text>Pessoa sem CNS e CPF</Text>}
      <HFlow hSpacing={0.25} alignItems='center'>
        <Text fontWeight='bold'>Idade: </Text>
        <Text>{humanizeAge(row.dataNascimentoCidadao)}</Text>
      </HFlow>
      <HFlow hSpacing={0.25} alignItems='center'>
        <Text fontWeight='bold'>Microárea: </Text>
        <Text>{row.microarea ?? 'Não informada'}</Text>
      </HFlow>
    </VFlow>
  )
}

export const renderTelefone = (row: BuscaCidadaoQueryResultModel) => {
  return !row?.telefoneCelular && !row?.telefoneContato && !row?.telefoneResidencial ? (
    <Text>-</Text>
  ) : (
    <HFlow hSpacing={0.25}>
      {row.telefoneResidencial && <Telefone value={row.telefoneResidencial} />}
      {row.telefoneCelular && (
        <Fragment>
          {row.telefoneResidencial && ' | '}
          <Telefone value={row.telefoneCelular} />
        </Fragment>
      )}
      {row.telefoneContato && (
        <Fragment>
          {(row.telefoneResidencial || row.telefoneCelular) && ' | '}
          <Telefone value={row.telefoneContato} />
        </Fragment>
      )}
    </HFlow>
  )
}

export const renderUltimasMedicoes = (row: BuscaCidadaoQueryResultModel) => (
  <VFlow vSpacing={0.2}>
    <Text component='p'>
      <Text fontWeight='bold'>Peso/Altura: </Text> {formatUltimaMedicaoPesoAltura(row)}
    </Text>
    <HFlow hSpacing={0.25}>
      <Text>
        <strong>P.A.:</strong> {formatUltimaMedicaoPressaoArterial(row)}
      </Text>
    </HFlow>
  </VFlow>
)

export const renderUltimoAtend = (
  {
    dataUltimoAtendimentoMedico,
    dataUltimoAtendimentoEnfermagem,
    dataUltimaVisitaDomiciliar,
    dataUltimoAtendimentoOdontologico,
  }: BuscaCidadaoQueryResultModel,
  getServerTimeNow: () => Date
) => {
  const atendimentos = [
    { title: 'Médico:', data: dataUltimoAtendimentoMedico },
    { title: 'Enfermagem:', data: dataUltimoAtendimentoEnfermagem },
    { title: 'Odontológico:', data: dataUltimoAtendimentoOdontologico },
    { title: 'Visita domiciliar:', data: dataUltimaVisitaDomiciliar },
  ]

  const now = getServerTimeNow()

  return (
    <VFlow vSpacing={0}>
      {atendimentos.map(({ title, data }) => {
        const parsedDate = toDate(data)

        return (
          <HLabel key={title} title={title} placeholder='-'>
            {parsedDate && formatDurationToMonthsAndDays(intervalToDuration({ start: parsedDate, end: now }))}
          </HLabel>
        )
      })}
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  warningTag: css`
    background-color: ${theme.pallete.status.alert.background};
    color: ${theme.pallete.status.alert.main};
    font-weight: normal;
    padding: 0.25rem 0.5rem 0.25rem 0.75rem;
  `,
})
